import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { ProductTypeCustomerRow } from '@metron/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DataTable } from '../components/data-table/data-table';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useReadRateSlaByProductType } from '../hooks/useReadRateSlaByProductType.ts';
import { sla1dColorClass, sla3dColorClass } from '../lib/utils';
import { ProductTypeFilterBar } from '../components/ProductTypeFilterBar';
import { useProductTypeFilters } from '../hooks/useProductTypeFilters.ts';
import { InstalledMetersTooltip } from '../components/InstalledMetersTooltip.tsx';
import { toErrorMessage } from '@metron/shared';

function formatSla(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(2)}%`;
}

const columns: ColumnDef<ProductTypeCustomerRow>[] = [
  {
    accessorKey: 'customerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => (
      <Link to={`/customers/${row.original.customerId}`} className='font-medium hover:underline'>
        {row.getValue('customerName')}
      </Link>
    ),
  },
  {
    accessorKey: 'accountType',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Type' />,
    cell: ({ row }) => row.original.accountType ?? '—',
  },
  {
    accessorKey: 'installedCount',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={<InstalledMetersTooltip>Installed</InstalledMetersTooltip>}
      />
    ),
    cell: ({ row }) => (
      <div className='text-right'>
        {(row.getValue('installedCount') as number).toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'meterCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Total' />,
    cell: ({ row }) => (
      <div className='text-right'>{(row.getValue('meterCount') as number).toLocaleString()}</div>
    ),
  },
  {
    id: 'reported1d',
    accessorKey: 'sensorsReported1d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='1-Day Reported' />,
    cell: ({ row }) => (
      <div className='text-right'>
        {row.original.sensorsReported1d.toLocaleString()} /{' '}
        {row.original.installedCount.toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'sla1d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='1-Day SLA' />,
    cell: ({ row }) => {
      const value = row.getValue('sla1d') as number | null;
      return (
        <div className={`text-right font-medium ${sla1dColorClass(value)}`}>
          {formatSla(value)}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.sla1d;
      const b = rowB.original.sla1d;
      if (a === null && b === null) return 0;
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    },
  },
  {
    id: 'reported3d',
    accessorKey: 'sensorsReported3d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='3-Day Reported' />,
    cell: ({ row }) => (
      <div className='text-right'>
        {row.original.sensorsReported3d.toLocaleString()} /{' '}
        {row.original.installedCount.toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'sla3d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='3-Day SLA' />,
    cell: ({ row }) => {
      const value = row.getValue('sla3d') as number | null;
      return (
        <div className={`text-right font-medium ${sla3dColorClass(value)}`}>
          {formatSla(value)}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.sla3d;
      const b = rowB.original.sla3d;
      if (a === null && b === null) return 0;
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    },
  },
];

export function ReadRateSlaByProductTypePage() {
  const { registerBuckets, meterBuckets, setRegister, setMeter } = useProductTypeFilters();

  const { data, isLoading, error } = useReadRateSlaByProductType({
    registerBuckets,
    meterBuckets,
  });
  // Unconstrained baseline for "Showing N of M" line. Cached separately by
  // TanStack Query because the queryKey differs.
  const { data: unconstrained } = useReadRateSlaByProductType({
    registerBuckets: [],
    meterBuckets: [],
  });

  const tableState = useTableUrlState({
    defaultSorting: [{ id: 'sla1d', desc: false }],
  });

  if (isLoading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Read-rate SLA by Product Type</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Read-rate SLA by Product Type</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>
              Error: {toErrorMessage(error)}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalMeters = data?.totalMeters ?? 0;
  const installedMeters = data?.installedMeters ?? 0;
  const baselineMeters = unconstrained?.totalMeters ?? 0;
  const customers = data?.customers ?? [];
  const asOfDate = data?.asOfDate ?? '';

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div>
        <div className='flex items-center justify-between'>
          <h1 className='text-3xl font-bold'>Read-rate SLA by Product Type</h1>
          <p className='text-muted-foreground'>As of {asOfDate}</p>
        </div>
        <p className='text-sm text-muted-foreground mt-1'>
          Filter the fleet by register type and meter size to see how each slice is performing.
        </p>
      </div>

      <ProductTypeFilterBar
        registerBuckets={registerBuckets}
        meterBuckets={meterBuckets}
        onRegisterChange={setRegister}
        onMeterChange={setMeter}
        trailing={
          <p className='text-sm text-muted-foreground'>
            Showing {totalMeters.toLocaleString()} of {baselineMeters.toLocaleString()} meters
          </p>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            <div>
              <div className='text-sm text-muted-foreground'>
                <InstalledMetersTooltip>Installed Meters</InstalledMetersTooltip>
              </div>
              <p className='text-2xl font-bold'>{installedMeters.toLocaleString()}</p>
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Total Meters</p>
              <p className='text-2xl font-bold'>{totalMeters.toLocaleString()}</p>
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>1-Day SLA</p>
              <p className={`text-2xl font-bold ${sla1dColorClass(data?.sla1d ?? null)}`}>
                {formatSla(data?.sla1d ?? null)}
              </p>
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>3-Day SLA</p>
              <p className={`text-2xl font-bold ${sla3dColorClass(data?.sla3d ?? null)}`}>
                {formatSla(data?.sla3d ?? null)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {customers.length === 0
        ? (
          <Card>
            <CardContent className='py-8'>
              <p className='text-center text-muted-foreground'>
                No customers with meters in this filter combination.
              </p>
            </CardContent>
          </Card>
        )
        : (
          <DataTable
            columns={columns}
            data={customers}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        )}
    </div>
  );
}
